import { useState, useCallback, createElement } from 'react';
import { resolveApiUrl } from '@/providers/api-url';
import { AffectedSubscribersDialog } from '@/components/shared/AffectedSubscribersDialog';

const API_URL = resolveApiUrl();

interface AffectedSubscriber {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

interface AffectedData {
  count: number;
  subscribers: AffectedSubscriber[];
}

interface CheckAndProceedParams {
  changeDescription: string;
  operationType: 'add' | 'remove' | 'update';
  onProceed: () => Promise<void>;
  onApplyToExisting?: () => Promise<void>;
  planId?: number;
  tierId?: number;
}

interface UseAffectedSubscribersCheckReturn {
  checkAndProceed: (params: CheckAndProceedParams) => void;
  dialogElement: React.ReactNode;
}

export function useAffectedSubscribersCheck(): UseAffectedSubscribersCheckReturn {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [affectedData, setAffectedData] = useState<AffectedData | null>(null);
  const [pendingParams, setPendingParams] = useState<CheckAndProceedParams | null>(null);

  const checkAndProceed = useCallback(
    (params: CheckAndProceedParams) => {
      const { planId, tierId } = params;

      if (!planId && !tierId) {
        // Нет ID — выполняем сразу (создание нового)
        void params.onProceed().catch(() => {});
        return;
      }

      const url = planId
        ? `${API_URL}/subscription-plans/admin/${planId}/affected-subscribers`
        : `${API_URL}/pricing-tiers/admin/${tierId}/affected-subscribers`;

      setPendingParams(params);

      fetch(url, {
        credentials: 'include',
      })
        .then((res) => res.json())
        .then((response: { success: boolean; data: AffectedData }) => {
          const affected = response.data;
          if (!affected || affected.count === 0) {
            // Нет затронутых подписчиков — выполняем сразу, без модалки
            setPendingParams(null);
            void params.onProceed().catch(() => {});
          } else {
            setAffectedData(affected);
            setDialogOpen(true);
          }
        })
        .catch(() => {
          // При ошибке запроса — выполняем сразу без модалки
          setPendingParams(null);
          void params.onProceed().catch(() => {});
        });
    },
    [],
  );

  const handleConfirm = useCallback(
    (applyToExisting: boolean) => {
      if (!pendingParams) {
        return;
      }

      const { onProceed, onApplyToExisting } = pendingParams;

      if (applyToExisting && onApplyToExisting) {
        if (pendingParams.operationType === 'remove') {
          void onApplyToExisting().then(() => onProceed()).catch(() => {});
        } else {
          void onProceed().then(() => onApplyToExisting()).catch(() => {});
        }
      } else {
        void onProceed().catch(() => {});
      }

      setDialogOpen(false);
      setPendingParams(null);
      setAffectedData(null);
    },
    [pendingParams],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open) {
        setPendingParams(null);
        setAffectedData(null);
      }
    },
    [],
  );

  const dialogElement = createElement(AffectedSubscribersDialog, {
    open: dialogOpen,
    onOpenChange: handleOpenChange,
    changeDescription: pendingParams?.changeDescription ?? '',
    operationType: pendingParams?.operationType ?? 'update',
    affectedData,
    isLoading: false,
    onConfirm: handleConfirm,
  });

  return {
    checkAndProceed,
    dialogElement,
  };
}
